import type { IngredientRef } from '@familychef/shared';
import {
  INGREDIENT_CATEGORIES,
  INGREDIENT_CATEGORY_LABELS,
  SEASON_LABELS,
  SEASONS,
} from '@familychef/shared';
import { Edit, LayoutGrid, Leaf, List, Plus, Snowflake, Sprout, Sun, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useDeleteIngredient, useIngredients } from '../../hooks/useIngredients';
import type { IngredientCategory, IngredientFilters, Season } from '../../hooks/useIngredients';
import { getPublicImageUrl } from '../../lib/supabase';

import { IngredientCsvActions } from './IngredientCsvActions';

import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { PageHeader } from '@/components/common/PageHeader';
import { SearchBar } from '@/components/common/SearchBar';
import { AppShell } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { useNavigate } from '@/lib/router';

const SEASON_ICONS: Record<string, React.ReactNode> = {
  spring: <Sprout size={14} />,
  summer: <Sun size={14} />,
  autumn: <Leaf size={14} />,
  winter: <Snowflake size={14} />,
};

const SEASON_COLORS: Record<string, string> = {
  spring: 'bg-green-100 text-green-700',
  summer: 'bg-amber-100 text-amber-700',
  autumn: 'bg-orange-100 text-orange-700',
  winter: 'bg-blue-100 text-blue-700',
};

export function IngredientsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [filters, setFilters] = useState<IngredientFilters>({
    search: '',
    category: '',
    season: '',
  });
  const [deleteTarget, setDeleteTarget] = useState<IngredientRef | null>(null);

  const { data, isLoading } = useIngredients(filters, page, pageSize);
  const deleteIngredient = useDeleteIngredient();

  const totalPages = Math.ceil((data?.total ?? 0) / pageSize);

  const columns = [
    {
      key: 'name',
      header: 'Nom',
      className: '',
      cell: (row: IngredientRef) => (
        <div className="flex items-center gap-3">
          {row.image_url ? (
            <img
              src={getPublicImageUrl('ingredients', row.image_url) ?? ''}
              alt={row.name}
              className="h-10 w-10 shrink-0 rounded-md bg-slate-100 object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400">
              <span className="text-xs">Aucune</span>
            </div>
          )}
          <span className="font-medium text-slate-900">{row.name}</span>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Catégorie',
      className: 'hidden sm:table-cell',
      cell: (row: IngredientRef) => (
        <span className="badge bg-slate-100 text-slate-600">
          {INGREDIENT_CATEGORY_LABELS[row.category]?.fr ?? row.category}
        </span>
      ),
    },
    {
      key: 'seasons',
      header: 'Saisons',
      className: 'hidden md:table-cell',
      cell: (row: IngredientRef) => (
        <div className="flex flex-wrap gap-1">
          {row.seasons.map((s) => (
            <span
              key={s}
              title={SEASON_LABELS[s]?.fr ?? s}
              className={`flex h-6 w-6 items-center justify-center rounded-full ${SEASON_COLORS[s] ?? 'bg-slate-100 text-slate-700'}`}
            >
              {SEASON_ICONS[s] ?? s}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'density',
      header: 'Densité (g/ml)',
      className: 'hidden lg:table-cell',
      cell: (row: IngredientRef) => (
        <span className="text-slate-600">{row.density_g_per_ml ?? '—'}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: '',
      cell: (row: IngredientRef) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => void navigate({ to: `/ingredients/${row.id}/edit` })}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title="Modifier"
          >
            <Edit size={15} />
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
            title="Supprimer"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <AppShell>
      <TopBar
        title="Ingrédients"
        subtitle={`${data?.total ?? 0} ingrédient${(data?.total ?? 0) > 1 ? 's' : ''} dans le référentiel`}
      />
      <div className="p-4 md:p-6">
        <PageHeader
          title="Référentiel d'ingrédients"
          actions={
            <>
              <IngredientCsvActions data={data?.data ?? []} />
              <button
                onClick={() => void navigate({ to: '/ingredients/new' })}
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
              >
                <Plus size={16} />
                Ajouter
              </button>
            </>
          }
        />

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchBar
            value={filters.search}
            onChange={(v) => {
              setFilters((f) => ({ ...f, search: v }));
              setPage(0);
            }}
            placeholder="Rechercher un ingrédient..."
          />
          <select
            value={filters.category}
            onChange={(e) => {
              setFilters((f) => ({ ...f, category: e.target.value as IngredientCategory | '' }));
              setPage(0);
            }}
            className="h-9 w-full sm:w-auto sm:flex-none flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-green-500 focus:outline-none"
          >
            <option value="">Toutes les catégories</option>
            {INGREDIENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {INGREDIENT_CATEGORY_LABELS[c]?.fr ?? c}
              </option>
            ))}
          </select>
          <select
            value={filters.season}
            onChange={(e) => {
              setFilters((f) => ({ ...f, season: e.target.value as Season | '' }));
              setPage(0);
            }}
            className="h-9 w-full sm:w-auto sm:flex-none flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-green-500 focus:outline-none"
          >
            <option value="">Toutes les saisons</option>
            {SEASONS.map((s) => (
              <option key={s} value={s}>
                {SEASON_LABELS[s]?.fr ?? s}
              </option>
            ))}
          </select>
          <div className="flex flex-1 sm:flex-none justify-end gap-3">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              className="h-9 w-auto rounded-lg border border-slate-200 bg-white px-2 text-sm focus:border-green-500 focus:outline-none"
            >
              {[20, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
            <div className="flex h-9 items-center rounded-lg border border-slate-200 bg-white p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`rounded-md px-2 py-1 ${viewMode === 'table' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                title="Vue tableau"
              >
                <List size={16} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`rounded-md px-2 py-1 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                title="Vue grille"
              >
                <LayoutGrid size={16} />
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'table' ? (
          <DataTable
            data={data?.data ?? []}
            columns={columns}
            isLoading={isLoading}
            getRowKey={(r) => r.id}
            emptyMessage="Aucun ingrédient trouvé"
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-10">
            {isLoading ? (
              <p className="col-span-full py-8 text-center text-sm text-slate-400">Chargement...</p>
            ) : data?.data && data.data.length > 0 ? (
              data.data.map((item) => (
                <div
                  key={item.id}
                  onClick={() => void navigate({ to: `/ingredients/${item.id}/edit` })}
                  className="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white hover:border-green-500 hover:shadow-sm"
                >
                  <div className="absolute inset-x-0 bottom-0 z-10 translate-y-full transform bg-white/95 p-3 backdrop-blur transition-transform duration-200 group-hover:translate-y-0">
                    <p className="mb-2 text-xs font-medium text-slate-500">
                      {INGREDIENT_CATEGORY_LABELS[item.category]?.fr ?? item.category}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {item.seasons.map((s) => (
                        <span
                          key={s}
                          title={SEASON_LABELS[s]?.fr ?? s}
                          className={`flex h-4 w-4 items-center justify-center rounded-full ${SEASON_COLORS[s] ?? 'bg-slate-100 text-slate-700'}`}
                        >
                          {SEASON_ICONS[s] ?? s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="aspect-square w-full bg-slate-50 relative">
                    {item.image_url ? (
                      <img
                        src={getPublicImageUrl('ingredients', item.image_url) ?? ''}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                        Aucune
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="truncate text-sm font-medium text-slate-900">{item.name}</h4>
                  </div>
                </div>
              ))
            ) : (
              <p className="col-span-full py-8 text-center text-sm text-slate-400">
                Aucun ingrédient trouvé
              </p>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Supprimer l'ingrédient"
        message={`Êtes-vous sûr de vouloir supprimer « ${deleteTarget?.name} » du référentiel ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        isDestructive
        isLoading={deleteIngredient.isPending}
        icon="🗑️"
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteIngredient.mutate(deleteTarget.id, {
            onSuccess: () => setDeleteTarget(null),
          });
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </AppShell>
  );
}
